'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, ImageFieldImage } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import clsx from 'clsx';

/**
 * Props for `ContentWithImageAndBullets`.
 */
export type ContentWithImageAndBulletsProps =
  SliceComponentProps<Content.ContentWithImageAndBulletsSlice>;

type Bullets = Content.ContentWithImageAndBulletsSlice['primary']['bullets'];

interface ImageSectionProps {
  main_image: ImageFieldImage;
}

// Image component
const ImageSection = ({ main_image }: ImageSectionProps) => (
  <div className="relative flex-shrink-0 w-full lg:w-auto">
    {main_image && (
      <div className="relative w-full lg:w-[416px] xl:w-[459px]">
        <PrismicNextImage
          field={main_image}
          className="w-full h-auto rounded-2xl object-cover"
          fallbackAlt=""
        />
      </div>
    )}
  </div>
);

interface BulletsSectionProps {
  side_heading: Content.ContentWithImageAndBulletsSlice['primary']['side_heading'];
  bullets: Bullets;
  isWhiteBg: boolean;
  bulletsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

// Bullets section component
const BulletsSection = ({
  side_heading,
  bullets,
  isWhiteBg,
  bulletsRef,
}: BulletsSectionProps) => (
  <div className="flex flex-col gap-6 flex-1">
    {side_heading && (
      <p
        className={clsx(
          'text-xl lg:text-2xl font-bold text-center lg:text-left',
          isWhiteBg ? 'text-[#041018]' : 'text-white',
        )}
      >
        {side_heading}
      </p>
    )}
    {bullets && bullets.length > 0 && (
      <div className="flex flex-col gap-6">
        {bullets.map((bullet, index) => (
          <div
            key={index}
            ref={(el) => {
              bulletsRef.current[index] = el;
            }}
            className={clsx(
              'rounded-2xl p-6 backdrop-blur-[10px]',
              isWhiteBg && 'border border-[#dedee9] bg-white',
            )}
            style={{
              borderRadius: '16px',
              background: isWhiteBg
                ? undefined
                : 'linear-gradient(180deg, #002726 0%, rgba(0, 0, 0, 0) 100%)',
              boxShadow: '0px 4.325px 64.871px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <div className="flex gap-3 items-center">
              {bullet.icon && (
                <div className="flex-shrink-0 w-12 h-12">
                  <PrismicNextImage
                    field={bullet.icon}
                    className="w-full h-full object-contain"
                    fallbackAlt=""
                  />
                </div>
              )}
              <div className="flex flex-col gap-3">
                {bullet.title && (
                  <p
                    className={clsx(
                      'text-lg lg:text-xl leading-[1.4] font-bold',
                      isWhiteBg ? 'text-[#041018]' : 'text-white',
                    )}
                  >
                    {bullet.title}
                  </p>
                )}
                {bullet.description && (
                  <p
                    className={clsx(
                      'text-lg lg:text-xl leading-[1.4] font-normal',
                      isWhiteBg ? 'text-[#4b5563]' : 'text-neutral-200',
                    )}
                  >
                    {bullet.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/**
 * Component for "ContentWithImageAndBullets" Slices.
 * Two variations:
 * - image_right_bullets: Image on the left, bullets on the right
 * - imageLeftWithBullets: Image on the right, bullets on the left
 */
const ContentWithImageAndBullets: FC<ContentWithImageAndBulletsProps> = ({
  slice,
}) => {
  const { title, description, main_image, side_heading, bullets } =
    slice.primary;

  // Determine if image should be on the left or right
  const isImageRight = slice.variation === 'image_right_bullets';
  const isWhiteBg = slice.variation === 'whiteBg';

  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bulletsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    if (contentRef.current) {
      const element = contentRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-y-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    bulletsRef.current.forEach((bulletEl, index) => {
      if (bulletEl) {
        currentElements.push(bulletEl);
        bulletEl.classList.add(
          'opacity-0',
          'translate-y-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        bulletEl.style.transitionDelay = `${(index + 1) * 100}ms`;
        observer.observe(bulletEl);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [bullets]);

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={clsx(
        'py-12 md:py-16 lg:py-20',
        isWhiteBg
          ? 'bg-[#0e1010] px-4 md:px-6 lg:px-8'
          : 'bg-[#0e1010] px-4 md:px-8 lg:px-[150px]',
      )}
    >
      <div
        className={clsx(
          'mx-auto',
          isWhiteBg
            ? 'rounded-[32px] bg-white px-6 py-12 shadow-[0px_18px_60px_rgba(0,0,0,0.18)] md:px-10 md:py-16 lg:px-16 lg:py-20'
            : 'max-w-[1140px]',
        )}
      >
        {/* Header Section */}
        {(title || description) && (
          <div
            ref={contentRef}
            className="flex flex-col gap-6 items-center text-center max-w-[874px] mx-auto mb-12 lg:mb-20"
          >
            {title && (
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h2
                      className={clsx(
                        'text-3xl md:text-4xl lg:text-[42px] font-medium leading-[1.2]',
                        isWhiteBg ? 'text-[#041018]' : 'text-white',
                      )}
                    >
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
            {description && (
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p
                      className={clsx(
                        'text-base lg:text-lg leading-[1.5]',
                        isWhiteBg ? 'text-[#041018]/80' : 'text-white/80',
                      )}
                    >
                      {children}
                    </p>
                  ),
                }}
              />
            )}
          </div>
        )}

        {/* Content Section - Image and Bullets */}
        <div
          className={clsx(
            'flex flex-col lg:flex-row gap-8 lg:gap-[60px] items-center justify-center',
            {
              'lg:flex-row-reverse':
                isImageRight || slice.variation === 'whiteBg',
            },
          )}
        >
          <ImageSection main_image={main_image} />
          <BulletsSection
            side_heading={side_heading}
            bullets={bullets}
            isWhiteBg={isWhiteBg}
            bulletsRef={bulletsRef}
          />
        </div>
      </div>
    </section>
  );
};

export default ContentWithImageAndBullets;
